/**
 * The صندوق نمونه behind the قرعه‌کشی landing (`/q/<code>`).
 *
 * Fabricated data that stands in for `cashbox_management` v1 responses, so the
 * landing can render the **real** cashbox screen — same header, same tabs, same
 * numbers formatting — to a visitor who has no account, no session and no box.
 * Nothing here ever touches the network.
 *
 * Why it lives next to the API client rather than in `modules/`: it is shaped
 * by the response contract in `types.d.ts` and has to move with it. The guards
 * in `index.ts` are the only callers.
 *
 * Three rules this fixture keeps, and they are not cosmetic:
 *
 *   1. **No phone numbers.** `MemberRow` prints `phone_number` when the caller
 *      is the owner, and the demo caller *is* the owner. A plausible-looking
 *      `09…` we invented is somebody's real number, so the field is simply
 *      absent — `ICashboxMembership.phone_number` is optional and the row
 *      falls back to the name.
 *   2. **The money adds up.** `balance` equals what the members below have
 *      actually paid, so «باقی‌مانده تا قرعه‌کشی» in the header is the real sum
 *      of what the unpaid members still owe. A demo whose arithmetic does not
 *      close is worse than no demo.
 *   3. **The dates are relative to today.** Hard-coded Jalali strings age into
 *      a box whose next draw is in the past, which is exactly how a landing
 *      reads as abandoned. Everything is derived from `moment()` at call time.
 */

import moment from 'jalali-moment'

import { longJalali } from 'utilities/helper/format'

/**
 * Sentinel id for the demo box.
 *
 * Non-numeric on purpose: every real cashbox id is a positive integer, so this
 * can never collide with one, and a stray `/cashbox/demo` would 404 rather
 * than quietly show someone else's box.
 */
export const DEMO_CASHBOX_ID = 'demo'

export const isDemoCashbox = (id?: string | number | null): boolean =>
    String(id ?? '') === DEMO_CASHBOX_ID

/* ------------------------------------------------------------------ dates */

/** The draw lands on the 15th of each Jalali month. */
const DAY_OF_DRAW = 15

/**
 * The draw date `offset` months from the current one, as a display string.
 *
 * `offset` 0 is the current month's draw, -1 the previous one, and so on.
 * Uses `jalali-moment` rather than month arithmetic on a Gregorian date: the
 * two calendars do not line up and «۱۵ شهریور» is not «15 September minus n».
 */
const drawDate = (offset: number): string =>
    longJalali(
        moment()
            .jDate(DAY_OF_DRAW)
            .add(offset, 'jMonth')
            .format('jYYYY-jMM-jDD')
    )

/**
 * Which draw is "next".
 *
 * Before the 15th the current month's draw is still ahead of us; after it, the
 * box is already collecting for next month. Without this the landing spends
 * half of every month advertising a draw date that has passed.
 */
const nextDrawOffset = (): number => (moment().jDate() <= DAY_OF_DRAW ? 0 : 1)

/* ------------------------------------------------------------------ money */

const SHARE_VALUE = 1_000_000
const TOTAL_SHARES = 12
const LOAN_AMOUNT = SHARE_VALUE * TOTAL_SHARES
const SATISFACTORY = SHARE_VALUE * TOTAL_SHARES

/* ---------------------------------------------------------------- members */

interface DemoMember {
    name: string
    role: CashboxRole
    /** whole shares held */
    shares: number
    /** still owed for the current cycle, in تومان */
    owes: number
    /** true once this member has taken their loan — drives «برنده شده» */
    hasWon: boolean
}

/**
 * Twelve people, one whole share each.
 *
 * Deliberately the simplest arrangement a صندوق can have: no خرده‌سهم groups,
 * no split shares, nobody holding two. This page is read in a few seconds by
 * someone who already runs a fund, and every extra structure on screen is
 * something they have to decode before they recognise their own صندوق in it.
 *
 * The first entry is the manager, and the landing visitor is shown the box
 * through their eyes — someone who already manages a fund is who this page is
 * for.
 */
const MEMBERS: ReadonlyArray<DemoMember> = [
    { name: 'مهدی رستمی', role: 'own', shares: 1, owes: 0, hasWon: false },
    { name: 'زهرا موسوی', role: 'nrm', shares: 1, owes: 0, hasWon: true },
    { name: 'علی‌رضا نجفی', role: 'nrm', shares: 1, owes: 0, hasWon: false },
    {
        name: 'فاطمه کریمی',
        role: 'nrm',
        shares: 1,
        owes: 1_000_000,
        hasWon: false,
    },
    { name: 'حسین قاسمی', role: 'nrm', shares: 1, owes: 0, hasWon: true },
    { name: 'مریم شریفی', role: 'nrm', shares: 1, owes: 0, hasWon: true },
    { name: 'امیر عبدالهی', role: 'mgr', shares: 1, owes: 0, hasWon: false },
    { name: 'سمیه اکبری', role: 'nrm', shares: 1, owes: 0, hasWon: false },
    {
        name: 'رضا صابری',
        role: 'nrm',
        shares: 1,
        owes: 1_000_000,
        hasWon: false,
    },
    { name: 'نرگس حیدری', role: 'nrm', shares: 1, owes: 0, hasWon: false },
    { name: 'محمد یوسفی', role: 'nrm', shares: 1, owes: 0, hasWon: false },
    { name: 'سعید فتحی', role: 'nrm', shares: 1, owes: 0, hasWon: true },
]

const membership = (member: DemoMember, index: number): ICashboxMembership => {
    const due = member.shares * SHARE_VALUE
    return {
        id: 1000 + index,
        member_id: 2000 + index,
        created: moment().subtract(6, 'months').toISOString(),
        name: member.name,
        role: member.role,
        // Deliberately absent — see the header comment. The owner view would
        // print it, and we are not inventing someone's phone number.
        number_of_shares: member.shares,
        share_residue: 0,
        share_group: null,
        commission: 0,
        balance: Math.max(0, due - member.owes),
        remaining_share_amount: member.owes,
        remaining_commission: 0,
        is_owner_accept: true,
        is_member_accept: true,
        local_score: member.owes === 0 ? 100 : 80,
        have_cashout: false,
        cashout_state: '',
        non_won_shares: member.hasWon ? 0 : member.shares,
        cashout_amount: 0,
        global_credit: 0,
    }
}

const MEMBERSHIPS: ReadonlyArray<ICashboxMembership> = MEMBERS.map(membership)

/** The manager's own membership — what «سهم شما» in the header reads. */
const OWNER_MEMBERSHIP = MEMBERSHIPS[0]

/** What the box actually holds: every share minus what is still owed. */
const BALANCE = MEMBERSHIPS.reduce((sum, m) => sum + m.balance, 0)

/* ---------------------------------------------------------------- winners */

interface DemoWinner {
    name: string
    cycle: number
}

/**
 * Every past winner is a member of `MEMBERS` with `hasWon: true`, and the test
 * holds the two in step — a برندگان tab listing somebody the اعضا tab does not
 * know is the kind of detail a careful reader notices immediately.
 */
const WINNERS: ReadonlyArray<DemoWinner> = [
    { name: 'مریم شریفی', cycle: 4 },
    { name: 'سعید فتحی', cycle: 3 },
    { name: 'حسین قاسمی', cycle: 2 },
    { name: 'زهرا موسوی', cycle: 1 },
]

const CURRENT_CYCLE = WINNERS.length + 1

/* ----------------------------------------------------------- transactions */

const transaction = (
    index: number,
    fields: Partial<ICashboxTransaction>
): ICashboxTransaction => ({
    pk: 5000 + index,
    amount: SHARE_VALUE,
    source: 'gtw',
    destination: 'cbx',
    state: 'suc',
    state_time: moment().subtract(index, 'days').toISOString(),
    created: moment().subtract(index, 'days').toISOString(),
    cycle_data: { cashbox_name: 'همدلی', cycle_index: CURRENT_CYCLE },
    ...fields,
})

/**
 * This cycle's cash-ins, newest first, plus last cycle's loan going out.
 *
 * Built from the members who have actually paid rather than typed out, so the
 * تراکنش‌ها tab can never disagree with the اعضا tab about who is settled.
 */
const TRANSACTIONS: ReadonlyArray<ICashboxTransaction> = [
    transaction(0, {
        amount: LOAN_AMOUNT,
        source: 'cbx',
        // `isOutgoing` in the transactions tab keys on exactly this.
        destination: 'mba',
        receiver_name: 'مریم شریفی',
        state: 'suc',
    }),
    ...MEMBERSHIPS.filter((m) => m.balance > 0).map((m, index) =>
        transaction(index + 1, {
            amount: m.balance,
            payer_name: m.name,
        })
    ),
]

/* ------------------------------------------------------------- public API */

/** `GET /cashbox_management/cashbox/demo/` */
export const demoCashboxDetail = (): ICashboxDetail => ({
    id: 0,
    name: 'همدلی',
    slug: 'hamdeli',
    state: 'act',
    type: 'csb',
    trust_state: 'trst',
    // کارمزدی, not اشتراکی: a `sub` box would render «نسخه‌ی صندوق مشخص نشده»
    // until an Order is paid, which is not the story this page is telling.
    plan: 'cms',
    image_id: 1,
    custom_avatar: null,
    period_index: 1,
    balance: BALANCE,
    bank_balance: BALANCE,
    hamyan_balance: 0,
    inprogress_balance: 0,
    owner_name: OWNER_MEMBERSHIP.name,
    manual_cashout: false,
    editable_members: true,
    notification_announce: 'all',
    sms_announce: 'all',
    under_maintenance: false,
    web_url: '',
    service_package: 0,
    service_package_remaining_days: null,
    mux_list: [],
    // Empty on purpose: a pending winner renders a warning that says payouts
    // happen in the Android app, which is the last thing a landing should say.
    last_cycle_unpaid_winners: [],
    period: {
        id: 1,
        index: 1,
        share_value: SHARE_VALUE,
        cycle_index: CURRENT_CYCLE,
        interval: 'm',
        day_of_draw: DAY_OF_DRAW,
        is_manager_first_winner: false,
        number_of_loans: 1,
        total_shares: TOTAL_SHARES,
        satisfactory_amount: SATISFACTORY,
        loan_amount: LOAN_AMOUNT,
        valid_memberships_count: MEMBERS.length,
        is_terminated: false,
        last_winner_name: WINNERS[0].name,
        current_period_cycle: CURRENT_CYCLE,
    },
    cycle: {
        id: 1,
        index: CURRENT_CYCLE,
        start_date: null,
        draw_date: null,
        draw_date_jalali: drawDate(nextDrawOffset()),
        // Undrawn, so `CashboxActions` offers the قرعه‌کشی button — the single
        // most persuasive control on the screen.
        drawing_date: null,
        drawing_date_jalali: '',
        is_manually_drawn: false,
        winner_name: '',
        days_to_draw: Math.max(
            0,
            moment()
                .jDate(DAY_OF_DRAW)
                .add(nextDrawOffset(), 'jMonth')
                .startOf('day')
                .diff(moment().startOf('day'), 'days')
        ),
    },
    membership: OWNER_MEMBERSHIP,
})

/** Case-insensitive contains, so the search box in the tabs really works. */
const matches = (haystack: string, q?: string) =>
    !q || haystack.includes(q.trim())

/**
 * `GET .../list-memberships-and-share-groups/`
 *
 * `share_groups` is always empty. The demo box has no خرده‌سهم by design, and
 * an empty list is what the real endpoint returns for such a box — so
 * `useShareGroupView` produces no `groupViews` and the «گروه‌های خرده‌سهم»
 * heading never renders.
 */
export const demoCashboxMembers = (q?: string): ICashboxMembersResponse => ({
    memberships: MEMBERSHIPS.filter((m) => matches(m.name, q)),
    share_groups: [],
    pagination: { has_next: false, has_previous: false },
})

/** `GET .../winners-list/` */
export const demoCashboxWinners = (
    q?: string
): { results: Array<ICashboxWinner> } => ({
    results: WINNERS.filter((w) => matches(w.name, q)).map((w, index) => ({
        id: 6000 + index,
        name: w.name,
        type: 'membership',
        cycle_index: w.cycle,
        // Relative to the *next* draw, not to today's month: once we are past
        // the 15th the current cycle has moved to next month and every past
        // cycle shifts with it, or the newest winner ends up dated in the
        // future.
        draw_date: drawDate(w.cycle - CURRENT_CYCLE + nextDrawOffset()),
        drawing_date: drawDate(w.cycle - CURRENT_CYCLE + nextDrawOffset()),
        loan_amount: LOAN_AMOUNT,
    })),
})

/** `GET .../transaction-list/` */
export const demoCashboxTransactions = (
    q?: string
): { results: Array<ICashboxTransaction> } => ({
    results: TRANSACTIONS.filter((t) =>
        matches(`${t.payer_name ?? ''} ${t.receiver_name ?? ''}`, q)
    ),
})
